/**
 * A `require()` for the three places the app deliberately uses one.
 *
 * ---------------------------------------------------------------------------
 * WHY THE WEB BUILD NEEDS THIS AT ALL
 * ---------------------------------------------------------------------------
 * `sdk-adapter.ts`, `register-native.ts` and `appVersion.ts` all load their
 * native dependency with a guarded `require(...)` - deliberately, so a build
 * missing the package fails inside a try/catch instead of at module load.
 * Metro resolves those calls; a browser has no `require` at all, so each one
 * would throw ReferenceError and be caught as "package absent".
 *
 * For most of them absent is exactly right on the web - there IS no
 * `@synquic/slide-voice-native` here, and `appVersion` falling back to its
 * constant is by design. But `@synquic/slide-voice` is, per its own package
 * description, "browser ready" - it is the signalling client over
 * livekit-client, and the browser supplies the WebRTC runtime natively. So
 * voice calls genuinely work on the web, provided that ONE require succeeds.
 *
 * This registers a global `require` that hands back the statically bundled
 * slide-voice and throws for everything else - which lands in the same
 * try/catch branches the native build already handles.
 *
 * Imported FIRST in `web/main.tsx`, before anything that might call it, for
 * the same hoisting reason `src/voice/register-native.ts` documents.
 */

import * as slideVoice from '@synquic/slide-voice';

const registry: Record<string, unknown> = {
  '@synquic/slide-voice': slideVoice,
};

(globalThis as Record<string, unknown>).require = (name: string): unknown => {
  if (name in registry) {
    return registry[name];
  }
  // Same outcome as a missing package under Metro: the caller's catch runs.
  throw new Error(`[web] module '${name}' is not part of the web bundle`);
};
